import { entityService } from '@/services/EntityService';
import { iemService, type PHRItem, type THRItem } from '@/services/iem';
import { httpClient } from '@/utils/httpClient';
import { outputReport, getOutputFormat } from '@/utils/reportOutput';
import {
  buildChangeDocumentReportHtml,
  type ChangeDocumentItem,
  type ChangeDocumentMemberInfo,
  type ChangeDocumentTemplateOptions,
} from '@/reports/templates/changeDocumentTemplate';
import { getPersonLabels } from '@/hooks/usePersonLabels';

interface TransactionLogAsset {
  assetId: string;
  lin: string;
  nsn: string;
  nomenclature: string;
  transQty: number;
}

interface TransactionLogDetail {
  id: string;
  transactionType: string;
  sourceWorkflow?: string;
  memberId: string;
  memberName: string;
  dateProcessed: string;
  assets: TransactionLogAsset[];
}

interface TransactionLogResponse {
  transactions: TransactionLogDetail[];
}

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const coerceString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeDateOnly = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const exactMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (exactMatch) {
    const [, yearText, monthText, dayText] = exactMatch;
    if (!yearText || !monthText || !dayText) {
      return null;
    }

    const year = Number.parseInt(yearText, 10);
    const month = Number.parseInt(monthText, 10);
    const day = Number.parseInt(dayText, 10);
    const date = new Date(Date.UTC(year, month - 1, day));

    if (
      date.getUTCFullYear() !== year ||
      date.getUTCMonth() + 1 !== month ||
      date.getUTCDate() !== day
    ) {
      return null;
    }

    return `${yearText}-${monthText}-${dayText}`;
  }

  const isoPrefixMatch = /^(\d{4})-(\d{2})-(\d{2})T/.exec(trimmed);
  if (isoPrefixMatch) {
    return `${isoPrefixMatch[1]}-${isoPrefixMatch[2]}-${isoPrefixMatch[3]}`;
  }

  return null;
};

const toTitle = (value: string): string =>
  value
    .toLowerCase()
    .split(/[_\s]+/g)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const formatRecoverableLabel = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const normalized = value.trim().toUpperCase();
  if (normalized.includes('NON')) return 'NonRecoverable';
  if (normalized.includes('RECOVERABLE')) return 'Recoverable';
  return value.trim();
};

const buildDueDateLookup = (thrItems: THRItem[], phrItems: PHRItem[]) => {
  const thrByAssetId = new Map<string, THRItem>();
  const phrByAssetId = new Map<string, PHRItem>();

  thrItems.forEach((item) => {
    if (item.asset_id) thrByAssetId.set(item.asset_id, item);
  });
  phrItems.forEach((item) => {
    if (item.asset_id) phrByAssetId.set(item.asset_id, item);
  });

  return { thrByAssetId, phrByAssetId };
};

const resolveChangeDocumentMember = (
  person: {
    display_name: string;
    entity_data?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
  } | null,
  billetDetail: string | null,
  dateOfChange: string | null,
  fallbackName: string,
): ChangeDocumentMemberInfo => {
  const entityData = (person?.entity_data ?? {}) as Record<string, unknown>;
  const metadata = (person?.metadata ?? {}) as Record<string, unknown>;

  const rank = coerceString(entityData.rank) ?? coerceString(metadata.rank);
  const org =
    coerceString(entityData.org) ?? coerceString(entityData.unit) ?? coerceString(metadata.unit);
  const section = coerceString(entityData.section) ?? coerceString(metadata.section);
  const phone = coerceString(entityData.phone) ?? coerceString(metadata.phone);
  const memberType =
    coerceString(entityData.type) ??
    coerceString(entityData.member_type) ??
    coerceString(metadata.type);

  const ssan =
    coerceString(entityData.ssan) ??
    coerceString(entityData.ssn) ??
    coerceString(entityData.individual_id) ??
    coerceString(metadata.ssan) ??
    coerceString(metadata.ssn) ??
    coerceString(metadata.individual_id) ??
    null;

  return {
    name: person?.display_name ?? fallbackName,
    ssan,
    memberType,
    rank,
    org,
    section,
    phone,
    billetDetail,
    dateOfChange,
  };
};

const buildBilletDetailLabel = (
  billet: Awaited<ReturnType<typeof iemService.getBilletForCustomer>>,
): string | null => {
  if (!billet) return null;
  return (
    billet.positionTitle?.trim() ||
    billet.dutyLocation?.trim() ||
    billet.deploymentStatus?.trim() ||
    null
  );
};

const isExchangeWorkflow = (transaction: TransactionLogDetail): boolean => {
  const type = transaction.transactionType?.toUpperCase?.() ?? '';
  if (type === 'EXCHANGE') return true;
  const source = transaction.sourceWorkflow?.toLowerCase() ?? '';
  return source.includes('exchange') || source.includes('dx');
};

const formatIssueTurnInDx = (transaction: TransactionLogDetail): string => {
  const type = transaction.transactionType?.toUpperCase?.() ?? '';
  if (type === 'TURN_IN') return 'Turn In';
  if (type === 'NON_RECOVERABLE_CLEAR') return 'Non-Recoverable';
  if (type === 'ISSUE_OUT') return 'Issue';
  return type ? toTitle(type) : '—';
};

const buildAggregateKey = (item: ChangeDocumentItem): string => {
  const issueKey = coerceString(item.issueTurnInDx)?.toUpperCase() ?? '';
  const nsnKey = coerceString(item.nsn)?.toUpperCase() ?? '';
  const linKey = coerceString(item.lin)?.toUpperCase() ?? '';
  const nomenclatureKey = item.nomenclature.trim().toUpperCase();

  if (nsnKey) return `${issueKey}|NSN:${nsnKey}`;
  if (linKey) return `${issueKey}|LIN:${linKey}`;
  return `${issueKey}|NOM:${nomenclatureKey}`;
};

const mergeDisplayValue = (
  current: string | null | undefined,
  incoming: string | null | undefined,
  mixedLabel: string,
): string | null => {
  const currentValue = coerceString(current);
  const incomingValue = coerceString(incoming);

  if (!currentValue && !incomingValue) return null;
  if (!currentValue) return incomingValue;
  if (!incomingValue) return currentValue;
  if (currentValue.toUpperCase() === incomingValue.toUpperCase()) return currentValue;
  return mixedLabel;
};

const aggregateItemsByNsn = (items: ChangeDocumentItem[]): ChangeDocumentItem[] => {
  const grouped = new Map<string, ChangeDocumentItem>();

  items.forEach((item) => {
    const key = buildAggregateKey(item);
    const quantity =
      typeof item.quantity === 'number' && Number.isFinite(item.quantity) ? item.quantity : 0;
    const existing = grouped.get(key);

    if (!existing) {
      grouped.set(key, {
        ...item,
        quantity,
      });
      return;
    }

    existing.quantity = (existing.quantity ?? 0) + quantity;
    existing.nsn = coerceString(existing.nsn) ?? coerceString(item.nsn);
    existing.lin = coerceString(existing.lin) ?? coerceString(item.lin);
    existing.recoverableFlag = mergeDisplayValue(
      existing.recoverableFlag,
      item.recoverableFlag,
      'Mixed',
    );
    existing.thrPhr = mergeDisplayValue(existing.thrPhr, item.thrPhr, 'Mixed');
    existing.dueBackDate = mergeDisplayValue(existing.dueBackDate, item.dueBackDate, 'Multiple');

    if (existing.qtyOnHand == null && item.qtyOnHand != null) {
      existing.qtyOnHand = item.qtyOnHand;
    }
    if (existing.unitCost == null && item.unitCost != null) {
      existing.unitCost = item.unitCost;
    }
    const newQty = existing.quantity ?? 0;
    existing.totalCost =
      typeof existing.unitCost === 'number' && Number.isFinite(existing.unitCost)
        ? existing.unitCost * newQty
        : null;

    if (existing.nomenclature === '—' && item.nomenclature !== '—') {
      existing.nomenclature = item.nomenclature;
    }
  });

  return Array.from(grouped.values());
};

const formatDateRangeLabel = (startDate: string | null, endDate: string | null): string | null => {
  if (!startDate && !endDate) return null;
  if (startDate && endDate && startDate === endDate) return startDate;
  if (startDate && endDate) return `${startDate} - ${endDate}`;
  return startDate ?? endDate;
};

export const runChangeDocumentReport = async (params: Record<string, unknown>): Promise<void> => {
  const personParam = params.person as { id: string; displayName: string } | null | undefined;
  const personId = personParam?.id ?? (params.personId as string | undefined)?.trim() ?? '';
  const startDate = normalizeDateOnly(params.startDate) ?? normalizeDateOnly(params.dateOfChange);
  const endDate = normalizeDateOnly(params.endDate) ?? startDate;

  if (!startDate || !endDate) {
    throw new Error('Start Date and End Date are required.');
  }

  const dateLabel = formatDateRangeLabel(startDate, endDate);

  let person: Awaited<ReturnType<typeof entityService.get>> | null = null;
  let billet: Awaited<ReturnType<typeof iemService.getBilletForCustomer>> | null = null;
  let thrItems: THRItem[] = [];
  let phrItems: PHRItem[] = [];

  if (personId) {
    [person, billet, thrItems, phrItems] = await Promise.all([
      entityService.get({ type: 'person', id: personId }),
      iemService.getBilletForCustomer(personId),
      iemService.getTHRItems(personId, { status: 'neq.closed' }),
      iemService.getPHRItems(personId, { status: 'neq.closed' }),
    ]);
  }

  const pl = getPersonLabels(params);
  const billetDetail = buildBilletDetailLabel(billet);
  const member = resolveChangeDocumentMember(
    person
      ? {
          display_name: person.display_name,
          entity_data: person.entity_data,
          metadata: person.metadata,
        }
      : null,
    billetDetail,
    dateLabel,
    `All ${pl.labelPlural}`,
  );

  const dueLookup = buildDueDateLookup(thrItems, phrItems);

  const txQuery = new URLSearchParams({ limit: '2000', types: 'TURN_IN,ISSUE_OUT,EXCHANGE' });
  txQuery.set('from', startDate);
  txQuery.set('to', endDate);

  const response = await httpClient.get<TransactionLogResponse>(
    `/api/v1/transactions/log?${txQuery.toString()}&source=report`,
  );
  const transactions = response.transactions ?? [];

  const memberTransactions = personId
    ? transactions.filter((tx) => tx.memberId === personId)
    : transactions;

  const assetIds = Array.from(
    new Set(
      memberTransactions
        .flatMap((tx) => tx.assets ?? [])
        .map((asset) => asset.assetId)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
    ),
  );

  const catalogIdByAssetId = new Map<string, string | null>();
  const qtyOnHandByAssetId = new Map<string, number | null>();
  const catalogIds: string[] = [];

  for (const batchIds of chunk(assetIds, 75)) {
    const idFilter = batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;
    const assets = await entityService.list({
      type: 'asset',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const asset of assets) {
      const entityData = (asset.entity_data ?? {}) as Record<string, unknown>;
      const rawCatalogId = entityData.catalog_id;
      const catalogId = typeof rawCatalogId === 'string' ? rawCatalogId.trim() : '';
      const normalized = catalogId.length > 0 ? catalogId : null;
      catalogIdByAssetId.set(asset.id, normalized);
      if (normalized) catalogIds.push(normalized);

      const rawQty = entityData.qty_on_hand ?? entityData.quantity_on_hand;
      qtyOnHandByAssetId.set(
        asset.id,
        typeof rawQty === 'number' && Number.isFinite(rawQty) ? rawQty : null,
      );
    }
  }

  const recoverableByCatalogId = new Map<string, string | null>();
  const unitCostByCatalogId = new Map<string, number | null>();

  const uniqueCatalogIds = Array.from(new Set(catalogIds));
  for (const batchIds of chunk(uniqueCatalogIds, 75)) {
    const idFilter = batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;
    const catalogs = await entityService.list({
      type: 'catalog',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const catalog of catalogs) {
      const entityData = (catalog.entity_data ?? {}) as Record<string, unknown>;
      const rawRecoverable = entityData.recoverable_nonrecoverable;
      recoverableByCatalogId.set(
        catalog.id,
        typeof rawRecoverable === 'string' ? formatRecoverableLabel(rawRecoverable) : null,
      );

      const rawPrice = entityData.unit_price ?? entityData.price ?? entityData.cost;
      const price = typeof rawPrice === 'number' ? rawPrice : typeof rawPrice === 'string' ? Number.parseFloat(rawPrice) : null;
      unitCostByCatalogId.set(
        catalog.id,
        typeof price === 'number' && Number.isFinite(price) ? price : null,
      );
    }
  }

  const rawItems: ChangeDocumentItem[] = memberTransactions.flatMap((transaction) => {
    const isExchange = isExchangeWorkflow(transaction);
    const issueTurnInDx = isExchange ? 'DX' : formatIssueTurnInDx(transaction);

    return (transaction.assets ?? []).flatMap((asset) => {
      const lin = asset.lin === '—' ? null : asset.lin;
      const nsn = asset.nsn === '—' ? null : asset.nsn;
      const nomenclature = asset.nomenclature || '—';
      const quantity =
        typeof asset.transQty === 'number' && Number.isFinite(asset.transQty) ? asset.transQty : 1;

      const thrItem = dueLookup.thrByAssetId.get(asset.assetId);
      const phrItem = dueLookup.phrByAssetId.get(asset.assetId);

      const thrPhr = thrItem ? 'THR' : phrItem ? 'PHR' : null;
      const dueBackDate = thrItem?.dueDate ?? phrItem?.extension?.dueDate ?? null;

      const catalogId = catalogIdByAssetId.get(asset.assetId) ?? null;
      const recoverableFlag = catalogId ? (recoverableByCatalogId.get(catalogId) ?? null) : null;
      const unitCost = catalogId ? (unitCostByCatalogId.get(catalogId) ?? null) : null;
      const qtyOnHand = qtyOnHandByAssetId.get(asset.assetId) ?? null;
      const totalCost = typeof unitCost === 'number' && typeof quantity === 'number'
        ? unitCost * quantity
        : null;

      const baseItem = {
        lin,
        nsn,
        nomenclature,
        quantity,
        qtyOnHand,
        unitCost,
        totalCost,
        recoverableFlag,
        thrPhr,
        dueBackDate,
      };

      if (isExchange) {
        return [
          { ...baseItem, issueTurnInDx: 'DX-TI' } satisfies ChangeDocumentItem,
          { ...baseItem, issueTurnInDx: 'DX-I' } satisfies ChangeDocumentItem,
        ];
      }

      return [{ ...baseItem, issueTurnInDx } satisfies ChangeDocumentItem];
    });
  });

  const items = aggregateItemsByNsn(rawItems);

  items.sort((a, b) => {
    const issueCompare = (a.issueTurnInDx ?? '').localeCompare(b.issueTurnInDx ?? '');
    if (issueCompare !== 0) return issueCompare;
    const nsnCompare = (a.nsn ?? '').localeCompare(b.nsn ?? '');
    if (nsnCompare !== 0) return nsnCompare;
    const linCompare = (a.lin ?? '').localeCompare(b.lin ?? '');
    if (linCompare !== 0) return linCompare;
    return a.nomenclature.localeCompare(b.nomenclature);
  });

  const options: ChangeDocumentTemplateOptions = {
    personLabels: pl,
    generatedAt: new Date(),
    member,
    items,
  };

  const html = buildChangeDocumentReportHtml(options);

  const safeName = personId
    ? member.name.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'member'
    : 'all-members';
  const filename = `change-document-${safeName}.html`;

  outputReport(html, filename, getOutputFormat(params));
};
